// Combat brains. One shared melee engagement core powers the training dummy,
// castle guards, the enemy assassin's fights, and uncontrolled player avatars
// defending themselves — parameterized by home position, aggro and leash.

import { attackSpec } from './attacks';
import {
  alarmPosts,
  assassinWaypoints,
  chamberRoute,
  patrolRoute,
  royalChambers,
} from './castle';
import { ARROW_GRAVITY, ARROW_LAUNCH_H, ARROW_SPEED, DT, TICK_RATE } from './constants';
import { PROFILES } from './profiles';
import { rngNext } from './rng';
import { musterPosition } from './castle';
import { plotOf } from './world';
import { archerRange } from './tech';
import { inFinalAssault, personality, slotPosition } from './squads';
import type { CharacterState, Command, GameState } from './types';
import { EMPTY_COMMAND } from './types';

interface MeleeBrainOpts {
  aggro: number;
  leash: number;
  /** Probability weights for the read game. */
  heavyChance: number;
  dodgeChance: number;
  kickTurtles: boolean;
  /** Walk back home when idle and further than this from home. */
  regroupDistance: number;
}

const TRAINING_OPTS: MeleeBrainOpts = {
  aggro: 14,
  leash: 26,
  heavyChance: 0.28,
  dodgeChance: 0.18,
  kickTurtles: true,
  regroupDistance: 1,
};

const GUARD_OPTS: MeleeBrainOpts = {
  aggro: 11,
  leash: 16,
  heavyChance: 0.2,
  dodgeChance: 0.08,
  kickTurtles: true,
  regroupDistance: 1.5,
};

/** Kings guard their spot: tiny aggro, barely any chase. */
const ROYAL_OPTS: MeleeBrainOpts = {
  aggro: 6,
  leash: 4,
  heavyChance: 0.15,
  dodgeChance: 0,
  kickTurtles: false,
  regroupDistance: 1,
};

/** Uncontrolled player avatars: fight back where they stand, never wander. */
const AUTO_DEFEND_OPTS: MeleeBrainOpts = {
  aggro: 5,
  leash: 3,
  heavyChance: 0.15,
  dodgeChance: 0.15,
  kickTurtles: false,
  regroupDistance: Infinity,
};

/** Nearest living enemy in range. Melee brains only see targets on their own
 * level (maxElevDiff 1.6 by default); archers pass Infinity — arrows arc. */
function nearestEnemy(
  state: GameState,
  c: CharacterState,
  range: number,
  maxElevDiff = 1.6
): CharacterState | null {
  let best = range;
  let found: CharacterState | null = null;
  for (const other of Object.values(state.characters)) {
    if (!other.alive || other.team === c.team) continue;
    if (Math.abs(other.elev - c.elev) > maxElevDiff) continue;
    const d = Math.hypot(other.x - c.x, other.z - c.z);
    if (d < best) {
      best = d;
      found = other;
    }
  }
  return found;
}

/** Vertical launch speed that lands an arrow on the target (fixed horizontal
 * speed, so flight time is distance / speed). */
function ballisticVy(c: CharacterState, target: CharacterState): number {
  const d = Math.hypot(target.x - c.x, target.z - c.z);
  const t = Math.max(0.15, d / ARROW_SPEED);
  const dy = target.elev + 1.2 - (c.elev + ARROW_LAUNCH_H);
  return dy / t + 0.5 * ARROW_GRAVITY * t;
}

/** Shared engagement core. Returns a command; mutates AI memory on c. */
function meleeBrainCommand(
  state: GameState,
  c: CharacterState,
  homeX: number,
  homeZ: number,
  opts: MeleeBrainOpts
): Command {
  const cmd: Command = { ...EMPTY_COMMAND };
  const p = PROFILES[c.profile];

  if (c.aiBlockTicks > 0) {
    c.aiBlockTicks -= 1;
    cmd.block = true;
  }

  const target = nearestEnemy(state, c, opts.aggro);

  if (!target || Math.hypot(c.x - homeX, c.z - homeZ) > opts.leash) {
    // no target (or strayed too far): head home
    const dx = homeX - c.x;
    const dz = homeZ - c.z;
    if (Math.hypot(dx, dz) > opts.regroupDistance) {
      cmd.moveX = dx;
      cmd.moveZ = dz;
    }
    return cmd;
  }

  const dx = target.x - c.x;
  const dz = target.z - c.z;
  const dist = Math.hypot(dx, dz);
  cmd.faceYaw = Math.atan2(dx, dz);

  if (c.stagger > 0 || c.dodging) return cmd;

  if (dist > p.lightAttackRange * 0.8) {
    cmd.moveX = dx;
    cmd.moveZ = dz;
    return cmd;
  }

  // react to an incoming swing: dodge or raise a block
  if (target.attacking && target.attackKind !== 'kick' && !c.attacking && !cmd.block) {
    if (c.aiCooldown <= 0) {
      const roll = rngNext(state);
      if (roll < opts.dodgeChance) {
        cmd.dodge = true;
        const side = rngNext(state) < 0.5 ? 1 : -1;
        cmd.moveX = Math.cos(c.heading) * side;
        cmd.moveZ = -Math.sin(c.heading) * side;
        c.aiCooldown = 0.7 + rngNext(state) * 0.5;
        return cmd;
      }
      if (roll < 0.65 && c.stamina / p.maximumStamina > 0.3) {
        c.aiBlockTicks = Math.floor((0.4 + rngNext(state) * 0.5) * TICK_RATE);
        cmd.block = true;
        return cmd;
      }
    }
  }

  // punish a held guard with a kick
  if (
    opts.kickTurtles &&
    target.blocking &&
    target.blockHeldTicks > 0.6 * TICK_RATE &&
    c.aiCooldown <= 0 &&
    !c.attacking &&
    rngNext(state) < 0.02
  ) {
    cmd.kick = true;
    cmd.block = false;
    c.aiBlockTicks = 0;
    c.aiCooldown = 0.8 + rngNext(state) * 0.6;
    return cmd;
  }

  // swing on a jittered cooldown
  if (c.aiCooldown <= 0 && !c.attacking && !cmd.block) {
    if (rngNext(state) < opts.heavyChance) {
      cmd.attackHeavy = true;
      const heavy = attackSpec(c.profile, 'heavy');
      c.aiCooldown = heavy.windup + heavy.recovery + 0.5 + rngNext(state) * 0.8;
    } else {
      cmd.attackLight = true;
      c.aiCooldown = 1.0 + rngNext(state) * 0.9;
    }
  }
  return cmd;
}

export function trainingCommand(state: GameState, c: CharacterState): Command {
  return meleeBrainCommand(state, c, c.spawnX, c.spawnZ, TRAINING_OPTS);
}

export function guardCommand(state: GameState, c: CharacterState): Command {
  if (c.archetype === 'archer') return wallArcherCommand(state, c);
  if (c.role === 'patrol' || c.role === 'wall' || c.role === 'sentry') {
    return patrolGuardCommand(state, c);
  }
  if (c.role === 'offduty') return offDutyCommand(state, c);
  const opts = c.profile === 'king' ? ROYAL_OPTS : GUARD_OPTS;
  return meleeBrainCommand(state, c, c.spawnX, c.spawnZ, opts);
}

/** Walk a patrol loop with loitering stops; fight anchored to the current
 * waypoint when something hostile comes close. */
function patrolGuardCommand(state: GameState, c: CharacterState): Command {
  const route = patrolRoute(c.team, c.patrolRoute ?? 'courtyard');
  const wp = route[c.aiWaypoint % route.length];
  const target = nearestEnemy(state, c, GUARD_OPTS.aggro);
  if (target) return meleeBrainCommand(state, c, wp.x, wp.z, GUARD_OPTS);

  const dx = wp.x - c.x;
  const dz = wp.z - c.z;
  const cmd: Command = { ...EMPTY_COMMAND };
  if (Math.hypot(dx, dz) > 1.0) {
    cmd.moveX = dx;
    cmd.moveZ = dz;
    return cmd;
  }
  if (c.aiCooldown <= 0) {
    c.aiWaypoint = (c.aiWaypoint + 1) % route.length;
    c.aiCooldown = 1.5 + rngNext(state) * 2.5; // linger at the post a moment
  }
  return cmd;
}

/** Off-duty guards idle by the well — until the alarm bell rings, when they
 * arm up and hold the inside of the gate. */
function offDutyCommand(state: GameState, c: CharacterState): Command {
  const alarmed = state.tick < state.alarmUntil[c.team];
  const post = alarmed
    ? alarmPosts(c.team)[c.squadIndex % 2]
    : { x: c.spawnX, z: c.spawnZ };
  const target = nearestEnemy(state, c, alarmed ? GUARD_OPTS.aggro : 8);
  if (target) return meleeBrainCommand(state, c, post.x, post.z, GUARD_OPTS);
  const dx = post.x - c.x;
  const dz = post.z - c.z;
  const cmd: Command = { ...EMPTY_COMMAND };
  if (Math.hypot(dx, dz) > 1.2) {
    cmd.moveX = dx;
    cmd.moveZ = dz;
    cmd.sprint = alarmed;
  }
  return cmd;
}

/** Garrison archers hold their battlement post and rain arrows on anything in
 * range below — they never leave the wall. */
function wallArcherCommand(state: GameState, c: CharacterState): Command {
  const cmd: Command = { ...EMPTY_COMMAND };
  // hold the post
  const dx = c.spawnX - c.x;
  const dz = c.spawnZ - c.z;
  if (Math.hypot(dx, dz) > 0.8) {
    cmd.moveX = dx;
    cmd.moveZ = dz;
    return cmd;
  }
  const target = nearestEnemy(state, c, 38, Infinity);
  if (!target) {
    cmd.faceYaw = c.team === 'friendly' ? Math.PI / 2 : -Math.PI / 2;
    return cmd;
  }
  cmd.faceYaw = Math.atan2(target.x - c.x, target.z - c.z);
  if (c.aiCooldown <= 0 && c.stagger <= 0) {
    cmd.shoot = true;
    cmd.shootVy = ballisticVy(c, target);
    c.aiCooldown = 1.5 + rngNext(state) * 0.6;
  }
  return cmd;
}

/** The AI King: retreats room-to-room when threatened, fights when cornered. */
export function royalCommand(state: GameState, c: CharacterState): Command {
  const chambers = royalChambers(c.team);
  const threat = nearestEnemy(state, c, 10);

  // a close threat sends the King fleeing to the next chamber in the chain
  if (
    threat &&
    c.aiChamber < chambers.length - 1 &&
    c.aiCooldown <= 0 &&
    Math.hypot(threat.x - c.x, threat.z - c.z) < 9
  ) {
    c.aiChamber += 1;
    c.aiWaypoint = 0;
    c.aiCooldown = 6; // don't chain-flee through every room at once
  }

  const route = chamberRoute(c.team, c.aiChamber);
  const wp = route[Math.min(c.aiWaypoint, route.length - 1)];
  const dw = Math.hypot(wp.x - c.x, wp.z - c.z);
  if (dw > 1.2) {
    const cmd: Command = { ...EMPTY_COMMAND };
    cmd.moveX = wp.x - c.x;
    cmd.moveZ = wp.z - c.z;
    cmd.sprint = threat !== null;
    return cmd;
  }
  if (c.aiWaypoint < route.length - 1) {
    c.aiWaypoint += 1;
    return { ...EMPTY_COMMAND };
  }

  // settled in a chamber: fight anyone who has cornered him
  if (threat && Math.hypot(threat.x - c.x, threat.z - c.z) < 4) {
    return meleeBrainCommand(state, c, c.x, c.z, ROYAL_OPTS);
  }
  return { ...EMPTY_COMMAND };
}

export function autoDefendCommand(state: GameState, c: CharacterState): Command {
  return meleeBrainCommand(state, c, c.x, c.z, AUTO_DEFEND_OPTS);
}

/** The mirror assassin: flank the battle via waypoints, fight through whatever
 * stands in the way, kill the opposing King. */
export function assassinCommand(state: GameState, c: CharacterState): Command {
  const king = Object.values(state.characters).find(
    (o) => o.kind === 'king' && o.team !== c.team
  );
  if (!king || !king.alive) {
    return { ...EMPTY_COMMAND };
  }

  const dKing = Math.hypot(king.x - c.x, king.z - c.z);
  // anyone in the way gets the full read game, no leash
  const blocker = nearestEnemy(state, c, 5);
  if (blocker || dKing < 12) {
    return meleeBrainCommand(state, c, c.x, c.z, {
      aggro: 12,
      leash: Infinity,
      heavyChance: 0.3,
      dodgeChance: 0.25,
      kickTurtles: true,
      regroupDistance: Infinity,
    });
  }

  // travel: follow the flanking route, sprinting while fresh
  const waypoints = assassinWaypoints(c.team);
  const wp = waypoints[Math.min(c.aiWaypoint, waypoints.length - 1)];
  const dx = wp.x - c.x;
  const dz = wp.z - c.z;
  if (Math.hypot(dx, dz) < 4 && c.aiWaypoint < waypoints.length - 1) {
    c.aiWaypoint += 1;
  }
  const cmd: Command = { ...EMPTY_COMMAND };
  cmd.moveX = dx;
  cmd.moveZ = dz;
  cmd.sprint = c.stamina / PROFILES[c.profile].maximumStamina > 0.5;
  return cmd;
}

// Soldier brain tuning. Expensive decisions (target scans) run on a staggered
// interval, not every tick — with ~100 soldiers this keeps AI cost trivial.
const SOLDIER_ENGAGE_RANGE = 12;
const SOLDIER_LEASH = 20;
const SOLDIER_REEVAL_TICKS = 18;

// Archer tuning (engagement range comes from tech via archerRange)
const ARCHER_MIN_RANGE = 6;
const ARCHER_AGGRO = 26;

/** True when no living friendly stands close to the firing line. */
function lineOfFireClear(
  state: GameState,
  c: CharacterState,
  target: CharacterState
): boolean {
  const dx = target.x - c.x;
  const dz = target.z - c.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-3) return true;
  const nx = dx / len;
  const nz = dz / len;
  for (const other of Object.values(state.characters)) {
    if (!other.alive || other.team !== c.team || other.id === c.id) continue;
    if (Math.abs(other.elev - c.elev) > 1.0) continue; // arrows arc over levels
    const ox = other.x - c.x;
    const oz = other.z - c.z;
    const along = ox * nx + oz * nz;
    if (along < 1 || along > len) continue;
    const across = Math.abs(ox * nz - oz * nx);
    if (across < 0.9) return false;
  }
  return true;
}

function archerCommand(state: GameState, c: CharacterState): Command {
  const cmd: Command = { ...EMPTY_COMMAND };
  const squad = state.squads[c.squadId!];
  const slot = slotPosition(squad, c.squadIndex);
  const leash = inFinalAssault(squad) ? 45 : SOLDIER_LEASH;

  if ((state.tick + c.squadIndex * 3) % SOLDIER_REEVAL_TICKS === 0) {
    const found = nearestEnemy(state, c, ARCHER_AGGRO, Infinity);
    c.aiTargetId = found ? found.id : null;
  }

  const target = c.aiTargetId ? state.characters[c.aiTargetId] : undefined;
  if (target && target.alive && Math.hypot(c.x - slot.x, c.z - slot.z) < leash) {
    const dx = target.x - c.x;
    const dz = target.z - c.z;
    const d = Math.hypot(dx, dz);
    cmd.faceYaw = Math.atan2(dx, dz);
    const elevated = c.elev - target.elev > 3; // shooting down over a parapet
    if (d < ARCHER_MIN_RANGE && Math.abs(target.elev - c.elev) < 1.6) {
      // too close: fall back while facing the threat
      cmd.moveX = -dx;
      cmd.moveZ = -dz;
    } else if (d <= archerRange(state, c.team)) {
      if (c.aiCooldown <= 0 && c.stagger <= 0 && (elevated || lineOfFireClear(state, c, target))) {
        cmd.shoot = true;
        cmd.shootVy = ballisticVy(c, target);
        c.aiCooldown = 1.35 + rngNext(state) * 0.4;
      }
    } else {
      cmd.moveX = dx;
      cmd.moveZ = dz;
    }
    return cmd;
  }

  const dx = slot.x - c.x;
  const dz = slot.z - c.z;
  const d = Math.hypot(dx, dz);
  if (d > 0.7) {
    cmd.moveX = dx;
    cmd.moveZ = dz;
    cmd.sprint = d > 7;
  } else {
    cmd.faceYaw = squad.facing;
  }
  return cmd;
}

/** A routed soldier flees for the home muster point, rallying on arrival. */
function routCommand(c: CharacterState): Command {
  const cmd: Command = { ...EMPTY_COMMAND };
  const muster = musterPosition(c.team);
  const dx = muster.x - c.x;
  const dz = muster.z - c.z;
  if (Math.hypot(dx, dz) < 14) {
    c.routing = false;
    c.morale = 0.55;
    return cmd;
  }
  cmd.moveX = dx;
  cmd.moveZ = dz;
  cmd.sprint = c.stamina / PROFILES[c.profile].maximumStamina > 0.25;
  return cmd;
}

export function soldierCommand(state: GameState, c: CharacterState): Command {
  // nerve: broken soldiers run; steady ones slowly recover
  if (!c.routing && c.morale < 0.25) {
    c.routing = true;
    c.aiTargetId = null;
  }
  if (c.routing) return routCommand(c);
  c.morale = Math.min(1, c.morale + 0.022 * DT);

  if (c.archetype === 'archer') return archerCommand(state, c);

  const cmd: Command = { ...EMPTY_COMMAND };
  const squad = state.squads[c.squadId!];
  const p = PROFILES[c.profile];
  const slot = slotPosition(squad, c.squadIndex);
  // temperament: hotheads push further to engage, disciplined men keep rank
  const pers = personality(c.id);
  const aggression = pers.aggression + (c.profile === 'veteranSoldier' ? 0.15 : 0);
  // once the assault has reached the keep, hunt wider so a hiding King is found
  const hunting = inFinalAssault(squad);
  const engageRange = hunting ? 40 : SOLDIER_ENGAGE_RANGE * aggression;
  const leash = hunting ? 45 : SOLDIER_LEASH * pers.discipline;

  // periodic target re-evaluation, staggered across the squad
  if ((state.tick + c.squadIndex * 3) % SOLDIER_REEVAL_TICKS === 0) {
    const found = nearestEnemy(state, c, engageRange);
    c.aiTargetId = found ? found.id : null;
    // badly outnumbered on the spot? that shakes anyone
    if (found) {
      let friends = 0;
      let foes = 0;
      for (const other of Object.values(state.characters)) {
        if (!other.alive || other.id === c.id) continue;
        if (Math.abs(other.x - c.x) > 9 || Math.abs(other.z - c.z) > 9) continue;
        if (Math.hypot(other.x - c.x, other.z - c.z) > 9) continue;
        if (other.team === c.team) friends++;
        else foes++;
      }
      if (foes > friends * 2 + 1) c.morale = Math.max(0, c.morale - 0.09);
    }
  }

  if (c.aiBlockTicks > 0) {
    c.aiBlockTicks -= 1;
    cmd.block = true;
  }

  const target = c.aiTargetId ? state.characters[c.aiTargetId] : undefined;
  if (target && target.alive) {
    // don't chase beyond the leash — the formation matters more than the kill
    if (Math.hypot(c.x - slot.x, c.z - slot.z) > leash) {
      c.aiTargetId = null;
    } else {
      const dx = target.x - c.x;
      const dz = target.z - c.z;
      const d = Math.hypot(dx, dz);
      cmd.faceYaw = Math.atan2(dx, dz);
      if (d > p.lightAttackRange * 0.85) {
        cmd.moveX = dx;
        cmd.moveZ = dz;
      } else if (c.aiCooldown <= 0 && !c.attacking && c.stagger <= 0 && !cmd.block) {
        if (target.attacking && rngNext(state) < 0.3) {
          c.aiBlockTicks = Math.floor((0.3 + rngNext(state) * 0.4) * TICK_RATE);
          cmd.block = true;
        } else {
          cmd.attackLight = true;
          c.aiCooldown = 1.1 + rngNext(state) * 1.0;
        }
      }
      return cmd;
    }
  }

  // raiders with no one to fight put buildings to the torch
  if (squad.objective === 'raid') {
    let bx = 0;
    let bz = 0;
    let bestD = 26;
    for (const b of state.buildings) {
      if (b.team === c.team || b.hp <= 0) continue;
      const p = plotOf(b.team, b);
      const d = Math.hypot(p.x - c.x, p.z - c.z);
      if (d < bestD) {
        bestD = d;
        bx = p.x;
        bz = p.z;
      }
    }
    if (bestD < 26) {
      const dx = bx - c.x;
      const dz = bz - c.z;
      cmd.faceYaw = Math.atan2(dx, dz);
      if (bestD > 3.6) {
        cmd.moveX = dx;
        cmd.moveZ = dz;
      } else if (c.aiCooldown <= 0 && !c.attacking && c.stagger <= 0) {
        cmd.attackLight = true;
        c.aiCooldown = 1.0 + rngNext(state) * 0.6;
      }
      return cmd;
    }
  }

  // regroup: head for the formation slot
  const dx = slot.x - c.x;
  const dz = slot.z - c.z;
  const d = Math.hypot(dx, dz);
  if (d > 0.7) {
    cmd.moveX = dx;
    cmd.moveZ = dz;
    cmd.sprint = d > 7;
  } else {
    cmd.faceYaw = squad.facing;
  }
  return cmd;
}

export function tickAiMemory(c: CharacterState): void {
  if (c.aiCooldown > 0) c.aiCooldown = Math.max(0, c.aiCooldown - DT);
}
